import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { SystemSeismograph } from '../components/SystemSeismograph';
export function WorkSystemOutput() {
  const {
    reportId
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const state = searchParams.get('state') || 'preview';
  const isUnlocked = state === 'full';
  const userData = {
    name: 'Anish',
    dateOfBirth: '19 June 1989',
    timeOfBirth: '2:50 PM',
    placeOfBirth: 'Surat, India'
  };
  // Data for the seismograph - Architect/Builder pattern
  // High exploration (left), spikes in strategic execution, low in routine execution (right)
  const systemPatternData = [{
    x: 0,
    y: 20
  }, {
    x: 15,
    y: 80
  }, {
    x: 30,
    y: 60
  }, {
    x: 45,
    y: 30
  }, {
    x: 60,
    y: 20
  }, {
    x: 80,
    y: 15
  }, {
    x: 100,
    y: 10
  }];
  const previewContent = {
    section1: {
      title: '1. Work System Architecture',
      subtitle: 'Builder, not operator.',
      content: `This system is configured for:

• designing structures
• solving complex problems
• creating frameworks
• thinking strategically

It is not configured for:

• maintenance work
• political navigation
• incremental optimization
• process compliance

This is an architect system, not an executor system.`
    },
    section2: {
      title: '2. Authority & Responsibility Pattern',
      subtitle: 'High agency, low tolerance for poor leadership.',
      content: `This system:

• resists vague authority
• questions poorly designed processes
• prefers autonomy over alignment
• takes responsibility when structure is missing

It does not follow blindly.

It follows when the structure makes sense.`
    }
  };
  const fullReportContent = {
    section3: {
      title: '3. Decision-Making Under Pressure',
      content: `Under work pressure, this system:

• slows down to think
• resists rushed decisions
• gets frustrated by reactive environments
• withdraws when forced to decide without clarity

This is not indecisiveness.

This is structural thinking that cannot be rushed.`
    },
    section4: {
      title: '4. Execution vs Leadership Dynamics',
      content: `This system:

• leads through clarity and structure
• struggles with ambiguous mandates
• prefers setting direction over following it
• gets impatient with consensus-driven processes

It is a strategic leader, not a collaborative manager.`
    },
    section5: {
      title: '5. Work Environment Fit',
      content: `Thrives in:

• greenfield projects
• high-autonomy roles
• founder or architect positions
• strategic problem-solving environments

Struggles in:

• politically complex organizations
• low-agency roles
• maintenance-heavy work
• consensus-driven cultures`
    },
    section6: {
      title: '6. Stress & Burnout Pattern',
      content: `This system burns out when:

• forced into maintenance mode
• stuck in incoherent structures
• required to execute without strategic input
• operating under poor leadership

Recovery requires:

• structural clarity
• autonomy restoration
• strategic re-engagement`
    },
    section7: {
      title: '7. Collaboration Style',
      content: `Works best with:

• competent, low-ego collaborators
• clear role definitions
• minimal political overhead
• shared commitment to quality

Struggles with:

• unclear ownership
• passive-aggressive dynamics
• consensus without clarity
• process over outcome`
    },
    section8: {
      title: '8. Career Trajectory Pattern',
      content: `This system:

• moves toward ownership and control
• leaves when structure degrades
• builds rather than climbs
• optimizes for leverage, not status

Long-term direction: founder, architect, or strategic advisor roles.`
    },
    section9: {
      title: '9. Work System Summary',
      content: `This is a high-agency, architecture-first work system.

It suffers in maintenance roles and thrives in building roles.

The goal is not to fix this system.

The goal is to place it correctly.`
    }
  };
  const handleUnlock = () => {
    navigate(`/report/work/${reportId}/unlock`);
  };
  const formatContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('•')) {
        return <div key={i} className="flex items-start gap-3 ml-4" data-id="element-5655">
            <span className="text-[#64748B] mt-1.5" data-id="element-5656">•</span>
            <span data-id="element-5657">{line.substring(1).trim()}</span>
          </div>;
      }
      if (line.trim() === '') {
        return <div key={i} className="h-4" data-id="element-5658" />;
      }
      return <p key={i} data-id="element-5659">{line}</p>;
    });
  };
  return <>
      <Helmet data-id="element-5660">
        <title data-id="element-5661">Work System Diagnostic — Report | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-5662" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-5663">
        <header data-id="element-5664">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-5665">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-5666">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-5667">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" data-id="element-5668" />
                <span className="text-lg font-medium tracking-tight" data-id="element-5669">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-5670">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-5671">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-5672">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-32 pb-24 px-6" data-id="element-5673">
          <div className="max-w-4xl mx-auto" data-id="element-5674">
            <section className="mb-16" data-id="element-5675">
              <div className="border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-5676">
                <div className="flex items-start justify-between mb-6" data-id="element-5677">
                  <h1 className="text-3xl md:text-4xl font-medium leading-tight tracking-tight" data-id="element-5678">
                    💼 Work System Diagnostic
                  </h1>
                  <div className={`px-3 py-1 border rounded text-xs uppercase tracking-wider flex items-center gap-2 ${isUnlocked ? 'bg-[#64748B]/20 border-[#64748B]/30 text-[#64748B]' : 'bg-[#2B2B2B]/20 border-[#2B2B2B] text-[#F5F5F5]/50'}`} data-id="element-5679">
                    <span data-id="element-5680">{isUnlocked ? '🟢' : '🟡'}</span>
                    {isUnlocked ? 'Full Diagnostic' : 'Preview'}
                  </div>
                </div>
                <div className="space-y-2 text-sm text-[#F5F5F5]/60 mb-6" data-id="element-5681">
                  <p data-id="element-5682">
                    <strong className="text-[#F5F5F5]/80" data-id="element-5683">Name:</strong>{' '}
                    {userData.name}
                  </p>
                  <p data-id="element-5684">
                    <strong className="text-[#F5F5F5]/80" data-id="element-5685">
                      Date of Birth:
                    </strong>{' '}
                    {userData.dateOfBirth}
                  </p>
                  <p data-id="element-5686">
                    <strong className="text-[#F5F5F5]/80" data-id="element-5687">Time:</strong>{' '}
                    {userData.timeOfBirth}
                  </p>
                  <p data-id="element-5688">
                    <strong className="text-[#F5F5F5]/80" data-id="element-5689">Place:</strong>{' '}
                    {userData.placeOfBirth}
                  </p>
                </div>
                <blockquote className="border-l-2 border-[#64748B] pl-6 italic text-[#F5F5F5]/80" data-id="element-5690">
                  A diagnostic of how your system operates in work,
                  responsibility, and authority.
                </blockquote>
              </div>
            </section>

            {/* System Map Visualization */}
            <section className="mb-16" data-id="element-5691">
              <div className="mb-6" data-id="element-5692">
                <h2 className="text-xl font-medium mb-2" data-id="element-5693">System Pattern Map</h2>
                <p className="text-sm text-[#F5F5F5]/60" data-id="element-5694">
                  Visualizing your system's energy distribution across the
                  Exploration-Execution spectrum.
                </p>
              </div>
              <SystemSeismograph data={systemPatternData} label="Work Architecture Pattern" data-id="element-5695" />
            </section>

            <section className="mb-16" data-id="element-5696">
              {!isUnlocked && <div className="mb-8 flex items-center gap-3" data-id="element-5697">
                  <div className="px-3 py-1 bg-[#64748B]/20 border border-[#64748B]/30 rounded text-xs text-[#64748B] uppercase tracking-wider" data-id="element-5698">
                    Preview Layer
                  </div>
                </div>}

              <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5699">
                <h2 className="text-2xl font-medium mb-3 tracking-tight" data-id="element-5700">
                  {previewContent.section1.title}
                </h2>
                <h3 className="text-lg font-medium mb-6 text-[#F5F5F5]/80" data-id="element-5701">
                  {previewContent.section1.subtitle}
                </h3>
                <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-5702">
                  {formatContent(previewContent.section1.content)}
                </div>
              </div>

              <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5703">
                <h2 className="text-2xl font-medium mb-3 tracking-tight" data-id="element-5704">
                  {previewContent.section2.title}
                </h2>
                <h3 className="text-lg font-medium mb-6 text-[#F5F5F5]/80" data-id="element-5705">
                  {previewContent.section2.subtitle}
                </h3>
                <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-5706">
                  {formatContent(previewContent.section2.content)}
                </div>
              </div>

              {!isUnlocked && <div className="border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-5707">
                  <p className="text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-5708">
                    🔒 This is the first layer of your Work System Diagnostic.
                  </p>
                  <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-5709">
                    The full report continues with:
                  </p>
                  <ul className="space-y-2 text-[#F5F5F5]/60 text-sm mb-6" data-id="element-5710">
                    <li data-id="element-5711">• Decision-making under pressure</li>
                    <li data-id="element-5712">• Execution vs leadership dynamics</li>
                    <li data-id="element-5713">• Work environment fit analysis</li>
                    <li data-id="element-5714">• Stress and burnout patterns</li>
                    <li data-id="element-5715">• Collaboration style mapping</li>
                    <li data-id="element-5716">• Career trajectory pattern</li>
                  </ul>
                </div>}
            </section>

            {!isUnlocked && <section className="mb-16" data-id="element-5717">
                <div className="border-2 border-[#64748B]/30 p-12 bg-[#111214] text-center" data-id="element-5718">
                  <Lock className="w-12 h-12 text-[#64748B] mx-auto mb-6" data-id="element-5719" />
                  <h2 className="text-3xl font-medium mb-6" data-id="element-5720">
                    Unlock Full Diagnostic
                  </h2>
                  <p className="text-lg text-[#F5F5F5]/70 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-5721">
                    This is not a different report. This is the rest of the same
                    diagnostic.
                  </p>

                  <div className="mb-12" data-id="element-5722">
                    <h3 className="text-sm font-medium text-[#F5F5F5]/50 mb-6 uppercase tracking-wider" data-id="element-5723">
                      Locked Sections
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto text-left" data-id="element-5724">
                      {['3. Decision-Making Under Pressure', '4. Execution vs Leadership Dynamics', '5. Work Environment Fit', '6. Stress & Burnout Pattern', '7. Collaboration Style', '8. Career Trajectory Pattern', '9. Work System Summary'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-5725">
                          <Lock className="w-4 h-4 text-[#64748B] flex-shrink-0 mt-0.5" data-id="element-5726" />
                          <span className="text-[#F5F5F5]/70 text-sm" data-id="element-5727">
                            {item}
                          </span>
                        </div>)}
                    </div>
                  </div>

                  <button onClick={handleUnlock} className="inline-block px-12 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-5728">
                    🔓 Unlock Full Diagnostic →
                  </button>

                  <p className="mt-6 text-xs text-[#F5F5F5]/40" data-id="element-5729">
                    One-time unlock · Complete diagnostic access
                  </p>
                </div>
              </section>}

            {isUnlocked && <section id="unlocked-content" data-id="element-5730">
                <div className="mb-8 flex items-center gap-3" data-id="element-5731">
                  <div className="px-3 py-1 bg-[#64748B]/20 border border-[#64748B]/30 rounded text-xs text-[#64748B] uppercase tracking-wider" data-id="element-5732">
                    Full Diagnostic
                  </div>
                </div>

                {Object.values(fullReportContent).map((section, i) => <div key={i} className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5733">
                    <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-5734">
                      {section.title}
                    </h2>
                    <div className="text-[#F5F5F5]/70 leading-relaxed space-y-2" data-id="element-5735">
                      {formatContent(section.content)}
                    </div>
                  </div>)}

                <div className="border border-[#2B2B2B] p-8 bg-[#111214]" data-id="element-5736">
                  <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-5737">
                    This report explains how your system operates in work
                    contexts. It does not tell you what to do, how to change, or
                    who to become. This is a diagnostic document, not a
                    prescription.
                  </p>
                </div>
              </section>}
          </div>
        </main>

        <footer className="py-8 px-6 border-t border-[#2B2B2B]" data-id="element-5738">
          <div className="max-w-7xl mx-auto text-center" data-id="element-5739">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-5740">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}